import { useEffect, useMemo, useState } from "react";
import { useMediaQuery, useTheme } from "@mui/material";
import {
  MRT_ColumnDef,
  MRT_TableOptions,
  MaterialReactTable,
  useMaterialReactTable
} from "material-react-table";
import { LocalizationProvider } from "@mui/x-date-pickers";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { download, generateCsv } from "export-to-csv";
import { useTranslation } from "react-i18next";
import { useSnackbar } from "notistack";
import { EquipmentStatus } from "../../../features/FloorManager/typings/svg.types";
import { useRemoteCreateEquipment } from "../../../hooks/Remote/Devices/useRemoteCreateEquipment";
import { useRemoteDeleteEquipment } from "../../../hooks/Remote/Devices/useRemoteDeleteEquipment";
import { useRemoteFetchEquipmentInventory } from "../../../hooks/Remote/Devices/useRemoteFetchEquipmentInventory";
import { useRemoteUpdateEquipment } from "../../../hooks/Remote/Devices/useRemoteUpdateEquipment";
import {
  EquipmentCategories,
  EquipmentCategory,
  checkTargetValError,
  checkTargetValueInventoryId,
  handleExportRows,
  validateEquipmentRow
} from "../equipmentlist.functions";
import { FmDeviceDialog as BookingPlanDialog } from "./FmDeviceDialog/FmDeviceDialog";
import { mapNewRowToPartialEquipmentInventory } from "./helpers/mapNewRowToPartialEquipmentInventory.function";
import { mapUpdatedRowToPartialEquipmentInventory } from "./helpers/mapUpdatedRowToPartialEquipmentInventory.function";
import { FilterableEquipmentListTopToolbar } from "./Toolbar/FilterableEquipmentListTopToolbar";
import { FilterableEquipmentListRowActions } from "./filterable-equipment-list-row-actions";
import { EquipmentRow, EquipmentRowValues } from "./typings/equipment-row.types";
import { commonLocalization } from "../../../functions/tableLocalization";
import TableDeleteConfirmDialog from "../../Common/TableDeletionConfirmDialog/TableDeletionConfirmDialog.component";
import {
  renderAssignedButton,
  renderCellBookable,
  renderEditEquipmentImage,
  renderEquipmentImage,
  renderSimpleCellName,
  serializeEquipment,
  SlidePanelCreateEdit,
  renderCellDepreciationDate,
  renderEditDepreciationDate,
  handleDepreciationFilterFn
} from "./filterable-equipment-list.partial";
import useTimeFormat from "../../../hooks/useTimeFormat/useTimeFormat";
import { EnlargedImageDialog } from "../../../features/EnlargedImageCarousel/enlarged-image-dialog.component";
import { FileObject } from "../../../features/FileDropzone/typings/FileObject.type";
import { TableExportPopper } from "../../Common/TableExportPopper/TableExportPopper.component";
import { withSeeditMrtInitialState } from "../../../themes/mrt-table-theme";
import { useSeeditMrtTableOptions } from "../../../themes/use-seedit-mrt-table-options";

/**
 * A tables that displays equipments and allows to update, delete and create new equipments.
 * It does not follow MVC-Patterns currently
 * todo refactor to follow MVC-Patterns
 * @param equipmentCategories
 */
export default function FilterableEquipmentList({
  equipmentCategories
}: Readonly<EquipmentCategories>) {
  const { t, i18n } = useTranslation();
  const { enqueueSnackbar } = useSnackbar();

  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("lg"));
  const separatorByLang = i18n.language === "de" ? ";" : ",";
  const { dateFormat } = useTimeFormat();

  const [data, setData] = useState<EquipmentRow[]>();
  const [open, setOpen] = useState<boolean>(false);

  const [selectedInventoryId, setSelectedInventoryId] = useState<number>();
  const [floorPlan, setFloorPlan] = useState<any>();
  const [dialogTitle, setDialogTitle] = useState<string>("Assigned Place");

  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const exportPopOpen = Boolean(anchorEl);

  const [validationErrors, setValidationErrors] = useState<Record<string, string | undefined>>({});
  const [isDeleteModalOpen, setIsDeleteModalOpen] = useState(false);
  const [deletionSelected, setDeletionSelected] = useState<number[] | null>(null);
  const [imageFile, setImageFile] = useState<{ file: FileObject[] }>({ file: [] });
  const [deleteRowImage, setDeleteRowImage] = useState<{
    rowId: number | null;
    picturePath: string | null;
  }>({ rowId: null, picturePath: null });

  const [showBiggerImage, setShowBiggerImage] = useState<string | null>(null);

  const {
    data: remoteEquipments,
    refetch: refetchRemoteEquipments,
    isLoading: isEquipmentsLoading,
    isFetching: isEquipmentsFetching
  } = useRemoteFetchEquipmentInventory();

  const { mutateAsync: removeEquipments, status: removeEquipmentStatus } =
    useRemoteDeleteEquipment();
  const { mutateAsync: addEquipments, status: addEquipmentStatus } = useRemoteCreateEquipment();
  const { mutateAsync: updateEquipments, status: updateEquipmentStatus } =
    useRemoteUpdateEquipment();

  // export to csv config
  const csvConfig = {
    fieldSeparator: separatorByLang,
    decimalSeparator: ".",
    useKeysAsHeaders: true,
    filename: t("equipment")
  };

  // handle table to export as csv`
  const handleExportData = () => {
    if (!data) return;
    const csvData = data.map(d => ({
      id: d.id,
      name: d.name,
      locationName: d.locationName,
      floorName: d.floorName,
      pricePerHour: d.pricePerHour,
      assetRefId: d.assetRefId,
      equipmentCategoryId: d.equipmentCategory?.id,
      equipmentCategoryName: d.equipmentCategory?.name,
      equipmentCategoryDescription: d.equipmentCategory?.description,
      equipmentCategoryTenantId: d.equipmentCategory?.tenantId,
      placeInventoryId: d.placeInventoryId,
      zoneInventoryId: d.zoneInventoryId,
      bookable: d.bookable === "true" ? t("Yes") : t("No")
      // status : d.status
    }));
    const csv = generateCsv(csvConfig)(csvData);
    download(csvConfig)(csv);
  };

  const column = useMemo<MRT_ColumnDef<EquipmentRow>[]>(
    () => [
      {
        accessorKey: "renderButton",
        header: "",
        enableColumnFilter: false,
        Cell: ({ row }) =>
          renderAssignedButton(row, setSelectedInventoryId, setFloorPlan, setOpen, setDialogTitle),
        size: 10,
        enableEditing: false,
        muiTableBodyCellProps: {
          align: "center",
          sx: { verticalAlign: "middle" }
        }
      },
      // {
      //   accessorKey: "status",
      //   header: "", // t("Status"),
      //   enableEditing: true,
      //   size: 60,
      //   editVariant: "select",
      //   editSelectOptions: () =>
      //     Object.values(EquipmentStatus).map((stat, index) => ({
      //       label: t(stat),
      //       value: stat,
      //       key: `${stat}${index}`
      //     })),
      //   Cell: ({ row }) => renderAssignedButton(row, setSelectedInventoryId, setFloorPlan, setOpen),
      //   muiEditTextFieldProps: {
      //     required: true,
      //     error: !!validationErrors?.status,
      //     helperText: validationErrors?.status,
      //     onFocus: () =>
      //       /* istanbul ignore next */
      //       setValidationErrors({
      //         ...validationErrors,
      //         status: undefined
      //       }),
      //     onChange: e => {
      //       /* istanbul ignore next */
      //       checkTargetValError(e.target.value, "status", validationErrors, setValidationErrors);
      //     }
      //   }
      // },
      {
        accessorKey: "picturePath",
        header: t("Image"),
        enableEditing: true,
        enableColumnFilter: false,
        enableSorting: false,
        Cell: ({ cell }) =>
          renderEquipmentImage(cell.row.original.picturePath, 100, setShowBiggerImage),
        Edit: ({ column, row }) =>
          renderEditEquipmentImage({
            header: column.columnDef.header,
            rowOriginal: row.original,
            imageFile,
            setImageFile,
            deleteRowImage,
            setDeleteRowImage
          }),
        size: 100
      },
      {
        accessorKey: "name",
        header: t("Name"),
        size: 200,
        enableEditing: true,
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.name,
          helperText: validationErrors?.name,
          onFocus: () =>
            /* istanbul ignore next */
            setValidationErrors({
              ...validationErrors,
              name: undefined
            }),
          onChange: e => {
            checkTargetValError(
              e.currentTarget.value,
              "name",
              validationErrors,
              setValidationErrors
            );
          }
        }
      },
      {
        accessorKey: "description",
        header: t("Description"),
        size: 200,
        enableEditing: true,
        muiEditTextFieldProps: {
          required: false
        }
      },
      {
        header: t("Category"),
        accessorKey: "equipmentCategory.name",
        size: 150,
        filterVariant: "multi-select",
        filterFn: "arrIncludesSome",
        filterSelectOptions: equipmentCategories.map(eqc => ({
          label: eqc.name,
          value: eqc.name,
          key: eqc.name
        })),
        editVariant: "select",
        editSelectOptions: () =>
          equipmentCategories.map(eqc => ({
            label: eqc.name,
            value: eqc.name,
            key: eqc.name
          }))
      },
      {
        header: t("Asset Id"),
        accessorKey: "assetRefId",
        size: 150
      },
      {
        accessorKey: "location.name", // accessorKey: "locationName",
        header: t("Location"),
        size: 100,
        enableEditing: false,
        Cell: ({ row: { original } }) =>
          renderSimpleCellName(original.location?.name || original.locationName)
      },
      {
        header: t("Floor"),
        accessorKey: "floor.name", // accessorKey: "floorName",
        size: 100,
        enableEditing: false,
        Cell: ({ row: { original } }) => {
          return renderSimpleCellName(original.floor?.name || original.floorName);
        }
      },
      {
        header: t("Zone Id"),
        accessorKey: "zoneInventoryId",
        enableEditing: true,
        size: 100,
        muiEditTextFieldProps: {
          required: false,
          error: !!validationErrors?.zoneInventoryId,
          helperText: validationErrors?.zoneInventoryId,
          onFocus: () => {
            /* istanbul ignore next */
            setValidationErrors({
              ...validationErrors,
              zoneInventoryId: undefined
            });
          },
          onChange: e => {
            /* istanbul ignore next */
            checkTargetValueInventoryId(
              e.currentTarget.value,
              "zoneInventoryId",
              t("Zone Inventory Id must be number"),
              validationErrors,
              setValidationErrors
            );
          }
        }
      },
      {
        header: t("Place Id"),
        accessorKey: "placeInventoryId",
        enableEditing: true,
        size: 100,
        muiEditTextFieldProps: {
          required: false,
          error: !!validationErrors?.placeInventoryId,
          helperText: validationErrors?.placeInventoryId,
          onFocus: () => {
            /* istanbul ignore next */
            setValidationErrors({
              ...validationErrors,
              placeInventoryId: undefined
            });
          },
          onChange: e => {
            /* istanbul ignore next */
            checkTargetValueInventoryId(
              e.currentTarget.value,
              "placeInventoryId",
              t("Place Inventory Id must be number"),
              validationErrors,
              setValidationErrors
            );
          }
        }
      },
      {
        header: t("Price (h)"),
        accessorKey: "pricePerHour",
        size: 30,
        muiEditTextFieldProps: { type: "number" }
      },
      {
        header: t("Bookable"),
        accessorKey: "bookable",
        size: 30,
        enableEditing: true,
        editVariant: "select",
        editSelectOptions: () => [
          { label: t("Yes"), value: "true" },
          { label: t("No"), value: "false" }
        ],
        filterVariant: "select",
        filterSelectOptions: [
          { label: t("Yes"), value: "true" },
          { label: t("No"), value: "false" }
        ],
        Cell: ({ row }) => renderCellBookable(row),
        muiEditTextFieldProps: {
          required: true,
          error: !!validationErrors?.bookable,
          helperText: validationErrors?.bookable,
          onFocus: () =>
            setValidationErrors({
              ...validationErrors,
              bookable: undefined
            }),
          onChange: e => {
            /* istanbul ignore next */
            checkTargetValError(e.target.value, "bookable", validationErrors, setValidationErrors);
          }
        }
      },
      {
        header: t("Depreciation Date"),
        accessorKey: "depreciationDate",
        accessorFn: row => (row.depreciationDate ? new Date(row.depreciationDate) : undefined), // convert to Date for sorting and filtering
        size: 50,
        enableEditing: true,
        Cell: ({ row }) => renderCellDepreciationDate(row, dateFormat),
        sortingFn: "datetime",
        filterVariant: "date-range",
        filterFn: (row, _columnId, filterValue) => {
          /* istanbul ignore next */
          return handleDepreciationFilterFn(row.original.depreciationDate, filterValue);
        },
        muiFilterDatePickerProps: {
          format: dateFormat.toUpperCase()
        },
        Edit: ({ column, row }) =>
          renderEditDepreciationDate({
            header: column.columnDef.header,
            rowOriginal: row.original
          })
      }
    ],
    [validationErrors, remoteEquipments, equipmentCategories, i18n.language, imageFile]
  );

  useEffect(() => {
    if (!remoteEquipments) return;

    setData(
      remoteEquipments.map(el => ({
        name: el.name,
        description: el.description,
        pricePerHour: el.pricePerHour,
        assetRefId: el.assetRefId,
        id: el.id,
        status: el.status ?? EquipmentStatus.AVAILABLE,
        locationName: el.locationName ?? "",
        floorName: el.floorName ?? "",
        equipmentCategory: el.equipmentCategory ?? { id: 0, name: "" },
        placeInventoryId: el.placeInventoryId,
        zoneInventoryId: el.zoneInventoryId,
        picturePath: el.picturePath ?? null,
        bookable: el.bookable === 0 ? "false" : "true",
        location: el.location ?? ({ name: el.locationName ?? "", id: 0 } as any),
        floor: el.floor ?? ({ name: el.floorName ?? "", id: 0 } as any),
        depreciationDate: el.depreciationDate ? el.depreciationDate : ""
      }))
    );
  }, [remoteEquipments]);

  // after deleting a row, refresh the list
  useEffect(() => {
    if (removeEquipmentStatus === "success") {
      const multiDeleted =
        deletionSelected && deletionSelected?.length > 1
          ? t("_deletedMultiEquipment")
          : t("_deletedSingleEquipment");

      enqueueSnackbar(t("Successfully deleted equipment", { unit: multiDeleted }), {
        variant: "success"
      });
      setIsDeleteModalOpen(false);
      refetchRemoteEquipments();
      setDeletionSelected(null);
    }
  }, [removeEquipmentStatus]);

  // after adding a eow, refresh the list
  useEffect(() => {
    if (addEquipmentStatus === "success") refetchRemoteEquipments();
  }, [addEquipmentStatus]);

  // after adding a eow, refresh the list
  useEffect(() => {
    if (updateEquipmentStatus === "success") refetchRemoteEquipments();
  }, [updateEquipmentStatus]);

  const resetTableCreateEdit = () => {
    setValidationErrors({});
    setImageFile({ file: [] });
  };

  const updateEquipmentDepreciationDate = (row: EquipmentRow, values: EquipmentRowValues) => {
    const upd = values;
    upd["depreciationDate"] = row.depreciationDate;

    return upd;
  };

  const updateEquipmentCategoryValue = (
    equipmentCategories: EquipmentCategory[],
    values: EquipmentRowValues
  ) => {
    const upd = values;
    const selectedEquipCate = equipmentCategories.find(
      c => upd["equipmentCategory.name"] === c.name
    );

    upd["equipmentCategory.id"] = selectedEquipCate?.id;
    upd["equipmentCategory.description"] = selectedEquipCate?.description;
    upd["equipmentCategory.tenantId"] = selectedEquipCate?.tenantId;

    return upd;
  };

  const updateEquipmentPicturepath = (values: EquipmentRowValues, rowOriginal: EquipmentRow) => {
    const upd = values;

    if (deleteRowImage.rowId === rowOriginal.id && deleteRowImage.picturePath === null) {
      upd["picturePath"] = undefined;
    }

    return upd;
  };

  /** deletes equipments */
  async function handleRowDelete(rowUids: number[]) {
    removeEquipments({ ids: [...rowUids] });
    table.resetRowSelection();
  }

  /** adds an equipment */
  const handleRowAdd: MRT_TableOptions<EquipmentRow>["onCreatingRowSave"] = async ({
    row,
    values,
    exitCreatingMode
  }) => {
    const newValidationErrors = validateEquipmentRow(values);
    if (Object.values(newValidationErrors).some(Boolean)) {
      return setValidationErrors(newValidationErrors);
    }

    resetTableCreateEdit();

    const updated1 = updateEquipmentCategoryValue(equipmentCategories, values);
    const updated2 = updateEquipmentDepreciationDate(row.original, updated1);
    const updated3 = updateEquipmentPicturepath(updated2, row.original);
    const newVal = mapNewRowToPartialEquipmentInventory(updated3);
    const newFormVal = serializeEquipment(newVal, row.original, imageFile.file);

    await addEquipments(newFormVal);
    exitCreatingMode();
    table.setCreatingRow(null); //exit creating mode
    setImageFile({ file: [] });
    setDeleteRowImage({ rowId: null, picturePath: null });
  };

  /** updates an equipment */
  const handleRowUpdate: MRT_TableOptions<EquipmentRow>["onEditingRowSave"] = async ({
    row,
    values,
    exitEditingMode
  }) => {
    const newValidationErrors = validateEquipmentRow(values);
    if (Object.values(newValidationErrors).some(Boolean)) {
      return setValidationErrors(newValidationErrors);
    }

    resetTableCreateEdit();

    const updated1 = updateEquipmentCategoryValue(equipmentCategories, values);
    const updated2 = updateEquipmentDepreciationDate(row.original, updated1);
    const updated3 = updateEquipmentPicturepath(updated2, row.original);
    const newVal = mapUpdatedRowToPartialEquipmentInventory(updated3);

    const newFormVal = serializeEquipment(newVal, row.original, imageFile.file);

    await updateEquipments(newFormVal);
    exitEditingMode();
    table.setEditingRow(null); // exit editing mode
    setImageFile({ file: [] });
    setDeleteRowImage({ rowId: null, picturePath: null });
  };
  const seeditMrtOptions = useSeeditMrtTableOptions<EquipmentRow>();

  const table = useMaterialReactTable({
    ...seeditMrtOptions,
    state: {
      isLoading: isEquipmentsLoading,
      isSaving:
        (addEquipmentStatus || updateEquipmentStatus || removeEquipmentStatus) === "loading",
      showProgressBars: isEquipmentsFetching
    },
    initialState: withSeeditMrtInitialState({
      showColumnFilters: true,
      showGlobalFilter: true,
      pagination: { pageSize: 10, pageIndex: 0 }
    }),
    createDisplayMode: "modal",
    editDisplayMode: "modal",
    autoResetPageIndex: false,
    enableDensityToggle: false,
    enableFullScreenToggle: false,
    enableHiding: true,
    enableRowSelection: true,
    enableEditing: true,
    columns: column ?? [],
    data: data ?? [],
    getRowId: row => row.id?.toString(),
    onCreatingRowCancel: resetTableCreateEdit,
    onCreatingRowSave: handleRowAdd,
    onEditingRowCancel: resetTableCreateEdit,
    onEditingRowSave: handleRowUpdate,
    positionToolbarAlertBanner: "none",
    muiPaginationProps: {
      rowsPerPageOptions: [5, 10, 15, 20, 30],
      variant: "outlined"
    },
    paginationDisplayMode: "pages",
    localization: {
      ...commonLocalization(t),
      noRecordsToDisplay: t("_editable_list_noRecords", { item: t("_noEquipment") })
    },
    renderRowActions: ({ row, table }) => {
      return (
        <FilterableEquipmentListRowActions
          row={row}
          table={table}
          setIsDeleteModalOpen={setIsDeleteModalOpen}
          setDeletionSelected={setDeletionSelected}
        />
      );
    },
    renderTopToolbar: ({ table }) => {
      return (
        <FilterableEquipmentListTopToolbar
          table={table}
          anchorEl={anchorEl}
          setAnchorEl={setAnchorEl}
          exportPopOpen={exportPopOpen}
          onReload={async () => {
            await refetchRemoteEquipments();
          }}
          disabledDeleteButton={table.getSelectedRowModel().flatRows.length <= 0}
          onDelete={() => {
            setIsDeleteModalOpen(true);

            const selected = table.getSelectedRowModel().rows.map(row => row.original.id);
            setDeletionSelected(selected);
          }}
        />
      );
    },
    renderCreateRowDialogContent: ({ table, row, internalEditComponents }) => (
      <SlidePanelCreateEdit
        table={table}
        row={row}
        internalEditComponents={internalEditComponents}
        headline={t("Add Equipment")}
      />
    ),
    renderEditRowDialogContent: ({ table, row, internalEditComponents }) => (
      <SlidePanelCreateEdit
        table={table}
        row={row}
        internalEditComponents={internalEditComponents}
        headline={t("Edit Equipment")}
      />
    )
  });

  return (
    <>
      <LocalizationProvider dateAdapter={AdapterDayjs}>
        <MaterialReactTable table={table} />
      </LocalizationProvider>

      {/* Confirm Deletion Modal */}
      <TableDeleteConfirmDialog
        dialogDesc={
          deletionSelected?.length === 1
            ? t("_tableSingleDeletionConfirmationDesc", { title: t("Equipment") })
            : t("_tableDeletionConfirmationDesc")
        }
        isDeleteModalOpen={isDeleteModalOpen}
        setIsDeleteModalOpen={setIsDeleteModalOpen}
        onConfirm={() => {
          /* istanbul ignore next */
          if (deletionSelected?.length) handleRowDelete(deletionSelected);
        }}
        deleteStatus={removeEquipmentStatus}
      />

      {/* export table button selecting csv or pdf */}
      <TableExportPopper
        exportPopOpen={exportPopOpen}
        anchorEl={anchorEl}
        handleExportCsv={handleExportData}
        handleExportPdf={() => handleExportRows(table.getPrePaginationRowModel().rows, t)}
      />

      <BookingPlanDialog
        open={open}
        /* istanbul ignore next */
        handleClose={() => setOpen(false)}
        isMobile={isMobile}
        floorPlan={floorPlan}
        selectedInventoryId={selectedInventoryId}
        dialogTitle={dialogTitle}
      />

      <EnlargedImageDialog
        open={!!showBiggerImage}
        picturePath={showBiggerImage}
        handleClose={() => setShowBiggerImage(null)}
      />
    </>
  );
}
